import axios from 'axios'
import qs from 'qs'
import { useAuthStore } from './stores/auth'
import { useErrorStore } from './stores/error'
import { useSystemStore } from './stores/system'
import { Str } from 'laravel-js-str'

let abortController = new AbortController()
let uploadedFiles = []
const api = axios.create({
  baseURL: import.meta.env.VITE_API_HOST,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
    'Content-Type': 'application/json'
  },
  paramsSerializer(query) {
    return qs.stringify(query, {
      encodeValuesOnly: true,
      arrayFormat: 'comma',
      skipNulls: true
    })
  }
})

api.interceptors.request.use(async (config) => {
  const errorStore = useErrorStore()
  const authStore = useAuthStore()
  const systemStore = useSystemStore()

  if (config.method === 'get' && authStore.authInitialized && !config.signal) {
    config.signal = abortController.signal
  } else {
    errorStore.clearSuccessMessage()
  }

  // Attach the bearer token
  if (localStorage.accessToken) {
    config.headers.Authorization = `Bearer ${localStorage.accessToken}`
  }

  if (config.method !== 'get') {
    uploadedFiles = []

    // Reset error messages
    errorStore.clearErrors()

    // Upload files directly to storage first
    if (config.headers.Precognition !== 'true') {
      config.data = await processFiles(config.data)
      systemStore.activeUpload = null
    }
    config.headers['Content-Type'] = 'application/json'
  }

  return config
})

async function processFiles(input) {
  for (const key in input) {
    if (input[key] instanceof File) {
      input[key] = await uploadFile(input[key])
      continue
    }

    if (Array.isArray(input[key]) || typeof input[key] === 'object') {
      input[key] = await processFiles(input[key])
      continue
    }
  }

  return input
}

async function getUploadSession() {
  const sessionApi = axios.create({
    baseURL: import.meta.env.VITE_API_HOST,
    headers: {
      Authorization: `Bearer ${localStorage.accessToken}`,
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    signal: abortController.signal
  })

  const response = await sessionApi.get('v3/utils/storage/session')
  return response.data
}

async function uploadFile(file) {
  const systemStore = useSystemStore()
  const session = await systemStore.getUploadSession()
  let filename = file.name
  let i = 0
  while (uploadedFiles.includes(filename)) {
    filename = Str.replaceLast('.', `_${++i}.`, file.name)
  }
  uploadedFiles.push(filename)
  filename = session.inputs.key.replace('${filename}', filename)

  const formData = new FormData()
  for (const key in session.inputs) {
    formData.append(key, key === 'key' ? filename : session.inputs[key])
  }
  formData.append('Content-Type', file.type)
  formData.append('file', file)

  const storage = axios.create()
  const config = {
    headers: session.local
      ? {
          Authorization: `Bearer ${localStorage.accessToken}`
        }
      : null,
    onUploadProgress: function (e) {
      systemStore.activeUpload = {
        name: file.name,
        size: e.total,
        transferred: e.loaded
      }
    },
    signal: abortController.signal
  }
  await storage.post(session.attributes.action, formData, config)

  return filename
}

api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    if (!error.response) {
      return Promise.reject(error)
    }
    const authStore = useAuthStore()
    const errorStore = useErrorStore()

    const status = error.response.status
    if (status === 401) {
      await authStore.initLogin()
    }
    if (status === 403 && error.response.data.message === 'Your email address is not verified.') {
      authStore.logOut()
    } else if (status === 422) {
      errorStore.setErrorMessage(error.response.data.message)
      errorStore.setErrors(error.response.data.message)
    } else if (status >= 400) {
      errorStore.setHttpError({ code: status, message: error.response.data.message })
    }
    return Promise.reject(error)
  }
)

async function abortAllRequests() {
  console.info('Switching page. Aborting all GET requests')
  await abortController.abort()
  abortController = new AbortController()
}

export { api as default, abortAllRequests, getUploadSession }
